import { useState, useEffect, useCallback, useRef } from 'react';
import { Lead, LEAD_TABLE_STATUS, LeadTableStatus, LeadGenProgress, AutoFindMoreStatus } from '../../types';
import type { Monitor } from '../../types/monitoring';
import { apiClient } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { useToast } from '../useToast';
import { subscribeToMonitorEvents } from '../../utils/monitorEvents';
import { useProductTour } from '../../contexts/ProductTourContext';
import { isTourDataset } from '../useTourMockData';
import { TOUR_MOCK_LEADS, TOUR_MOCK_COLUMNS, TOUR_MOCK_DATASET } from '../../constants/tourMockData';

export interface TableColumn {
  key: string;
  name: string;
  isSegment?: boolean;
}

export interface TableDataState {
  leads: Lead[];
  name: string;
  type: 'companies' | 'people' | 'custom';
  columns: TableColumn[];
  tableData: any;
  status: LeadTableStatus;
  errorMessage: string | undefined;
  prospectsAnalyzed: number | null;
  monitors: Monitor[];
  isLoading: boolean;
  progressData: LeadGenProgress | null;
}

export interface UseTableDataReturn extends TableDataState {
  setProgressData: (data: LeadGenProgress | null) => void;
  refreshData: () => Promise<void>;
  updateDataset: (tableId: string) => void;
}

interface UseTableDataProps {
  tableId: string | undefined;
  onDatasetUpdated?: (tableId: string) => void;
}

export const useTableData = ({ tableId, onDatasetUpdated }: UseTableDataProps): UseTableDataReturn => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [name, setName] = useState<string>('');
  const [type, setType] = useState<'companies' | 'people' | 'custom'>('companies');
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [tableData, setTableData] = useState<any>(null);
  const [status, setStatus] = useState<LeadTableStatus>(LEAD_TABLE_STATUS.GENERATING);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [prospectsAnalyzed, setProspectsAnalyzed] = useState<number | null>(null);
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [progressData, setProgressData] = useState<LeadGenProgress | null>(null);

  const { showToast } = useToast();
  const { isActive: isTourActive } = useProductTour();
  const isViewingTourDataset = isTourActive && isTourDataset(tableId);

  // Track previous auto_find_more status to detect completion
  const prevAutoFindMoreStatus = useRef<string | null>(null);

  // Subscribe to real-time table status updates
  useEffect(() => {
    if (!tableId) return;

    const channel = supabase
      .channel(`table-${tableId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_tables',
          filter: `id=eq.${tableId}`
        },
        (payload) => {
          const newStatus = payload.new.status;
          const newErrorMessage = payload.new.error_message;

          if (newStatus) {
            setStatus(newStatus as LeadTableStatus);
            setErrorMessage(newErrorMessage);
            setTableData(payload.new);

            if (payload.new.provider_metadata) {
              const metadata = payload.new.provider_metadata;
              if (metadata.total_analyzed !== undefined) {
                setProspectsAnalyzed(metadata.total_analyzed);
              }

              // Check for auto find-more completion and show toast
              const autoFindMore = metadata.auto_find_more as AutoFindMoreStatus | undefined;
              const prevStatus = prevAutoFindMoreStatus.current;

              if (autoFindMore && prevStatus === 'in_progress' && autoFindMore.status !== 'in_progress') {
                const totalFound = metadata.total_found || 0;
                const requested = metadata.row_count || totalFound;

                if (autoFindMore.status === 'exhausted') {
                  showToast(
                    `Found ${totalFound} leads - no more results available`,
                    'info'
                  );
                } else if (autoFindMore.status === 'max_reached') {
                  showToast(
                    `Found ${totalFound} of ${requested} requested leads`,
                    'info'
                  );
                }
                // 'completed' status doesn't need a toast - success is obvious
              }

              // Update previous status for next comparison
              prevAutoFindMoreStatus.current = autoFindMore?.status || null;
            }

            if (newStatus.startsWith('error')) {
              showToast(
                newErrorMessage || 'An error occurred during lead generation',
                'error'
              );
            } else if (newStatus === LEAD_TABLE_STATUS.COMPLETED_NO_RESULTS) {
              showToast(
                newErrorMessage || 'No matching results found. Try adjusting your search criteria.',
                'warning'
              );
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      // Reset auto find-more status when tableId changes to prevent stale status
      // from triggering completion toasts for a different table
      prevAutoFindMoreStatus.current = null;
    };
  }, [tableId, showToast]);

  // Load leads when tableId changes
  useEffect(() => {
    const loadTableData = async () => {
      if (!tableId) {
        setLeads([]);
        setColumns([]);
        setProspectsAnalyzed(null);
        setTableData(null);
        setStatus(LEAD_TABLE_STATUS.GENERATING);
        setMonitors([]);
        return;
      }

      // Return mock data for tour dataset
      if (isViewingTourDataset) {
        const columnData = TOUR_MOCK_COLUMNS.map((colName: string) => ({
          key: colName.toLowerCase().replace(/\s+/g, '_'),
          name: colName,
          isSegment: false
        }));
        setLeads(TOUR_MOCK_LEADS);
        setColumns(columnData);
        setName(TOUR_MOCK_DATASET.name);
        setType(TOUR_MOCK_DATASET.table_type as 'companies' | 'people' | 'custom');
        setStatus(LEAD_TABLE_STATUS.COMPLETED);
        setTableData(TOUR_MOCK_DATASET);
        setProspectsAnalyzed(TOUR_MOCK_LEADS.length);
        setMonitors([]);
        setErrorMessage(undefined);
        setIsLoading(false);
        return;
      }

      try {
        setLeads([]);
        setColumns([]);
        setProspectsAnalyzed(null);
        setTableData(null);
        setName('');
        setStatus(LEAD_TABLE_STATUS.GENERATING);
        setIsLoading(true);

        const { data: tableDataResult, error: tableError } = await supabase
          .from('lead_tables')
          .select('*')
          .eq('id', tableId)
          .single();

        if (tableError) throw tableError;

        setName(tableDataResult?.name || 'Dataset');
        setType(tableDataResult?.table_type || 'companies');
        setErrorMessage(tableDataResult?.error_message);

        const data = await apiClient.getTableLeads(tableId);
        const { leads: fetchedLeads, columns: fetchedColumns } = data;

        const analyzedCount =
          tableDataResult?.provider_metadata?.total_analyzed ??
          tableDataResult?.prospects_analyzed ??
          data.prospects_analyzed ??
          null;
        setProspectsAnalyzed(analyzedCount);

        const columnData = fetchedColumns.map((colName: string) => ({
          key: colName.toLowerCase().replace(/\s+/g, '_'),
          name: colName,
          isSegment: false
        }));

        setLeads(fetchedLeads);
        setColumns(columnData);
        setTableData(tableDataResult);

        if (tableDataResult?.status) {
          setStatus(tableDataResult.status as LeadTableStatus);
        } else {
          setStatus(LEAD_TABLE_STATUS.GENERATING);
        }

        // Fetch monitors for this dataset (clear first to avoid stale data when switching)
        setMonitors([]);
        try {
          const monitorsResult = await apiClient.listMonitors(tableId);
          setMonitors(monitorsResult.monitors);
        } catch (monitorError) {
          console.error('Error fetching monitors:', monitorError);
        }
      } catch (error) {
        console.error('Error loading table data:', error);
        setLeads([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTableData();
  }, [tableId, isViewingTourDataset]);

  // Subscribe to monitor events to refresh monitors state when chat actions modify them
  useEffect(() => {
    if (!tableId) return;

    const unsubscribe = subscribeToMonitorEvents(async (_eventType, detail) => {
      // Refresh monitors if the event is for this table or no specific table
      if (!detail?.tableId || detail.tableId === tableId) {
        try {
          const monitorsResult = await apiClient.listMonitors(tableId);
          setMonitors(monitorsResult.monitors);
        } catch (error) {
          console.error('Error refreshing monitors:', error);
        }
      }
    });

    return unsubscribe;
  }, [tableId]);

  const refreshData = useCallback(async () => {
    if (!tableId) return;

    // Don't refresh if viewing tour dataset - keep mock data
    if (isViewingTourDataset) return;

    try {
      setIsLoading(true);

      const { data: tableDataResult, error: tableError } = await supabase
        .from('lead_tables')
        .select('*')
        .eq('id', tableId)
        .single();

      if (tableError) throw tableError;

      setName(tableDataResult?.name || 'Dataset');
      setType(tableDataResult?.table_type || 'companies');
      setErrorMessage(tableDataResult?.error_message);

      if (tableDataResult?.provider_metadata?.total_analyzed !== undefined) {
        setProspectsAnalyzed(tableDataResult.provider_metadata.total_analyzed);
      }

      const { leads: fetchedLeads, columns: fetchedColumns } = await apiClient.getTableLeads(tableId);

      const columnData = fetchedColumns.map((colName: string) => ({
        key: colName.toLowerCase().replace(/\s+/g, '_'),
        name: colName,
        isSegment: false
      }));

      setLeads(fetchedLeads);
      setColumns(columnData);
      setTableData(tableDataResult);
      setStatus(tableDataResult?.status || LEAD_TABLE_STATUS.COMPLETED);
    } catch (error) {
      console.error('Error loading table data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [tableId, isViewingTourDataset]);

  const updateDataset = useCallback((tableId: string) => {
    onDatasetUpdated?.(tableId);
  }, [onDatasetUpdated]);

  return {
    leads,
    name,
    type,
    columns,
    tableData,
    status,
    errorMessage,
    prospectsAnalyzed,
    monitors,
    isLoading,
    progressData,
    setProgressData,
    refreshData,
    updateDataset,
  };
};
